import * as React from 'react';
import {connect} from 'react-redux';

import type {
    ComparedActivityReportSummary,
    ComparisonRangeType,
    ReportRowData,
} from 'nutshell-core/types';

import type {NutshellState} from '../../../store';

import type {SortParams} from '../report-types';

import * as actions from './activity-report-actions';
import * as selectors from './activity-report-selectors';
import {ActivityReportSummaryTable} from './activity-report-summary-table';
import {USER_SORT_STRING, SORT_DIRECTION_ASCENDING, SORT_DIRECTION_DESCENDING} from './constants';
import {sortSummaryData, checkDataCanBeSortedByValue} from './sort-summary-data';

const mapStateToProps = (state: NutshellState, ownProps: OwnProps): StateProps => {
    const rawData = selectors.getSummaryData(state);

    if (ownProps.activeSort && !checkDataCanBeSortedByValue(rawData, ownProps.activeSort.by)) {
        // We can't sort the data by the current sort by value, probably because
        // of an applied filter. Therefore, reset the sort value to default
        ownProps.onParamChange({sort: undefined}, false);
    }

    const data = rawData ? sortSummaryData(rawData, ownProps.activeSort) : null;

    return {
        isLoading: selectors.getSummaryIsLoading(state),
        hasError: selectors.getSummaryHasError(state),
        totals: selectors.getSummaryTotals(state),
        // @ts-expect-error
        data,
    };
};

// @ts-expect-error
const mapDispatchToProps = (dispatch): DispatchProps => {
    return {
        fetchContent: (filters, comparisonType) => {
            dispatch(actions.requestActivityReportSummary({filters, comparisonType}));
        },
    };
};

type StateProps = {
    isLoading: boolean;
    hasError: boolean;
    data: ComparedActivityReportSummary<Array<ReportRowData>> | null | undefined;
    totals: ComparedActivityReportSummary<ReportRowData> | null | undefined;
};
type DispatchProps = {
    fetchContent: (filters: any, comparisonType?: ComparisonRangeType | null | undefined) => any;
};
type OwnProps = {
    filters: any;
    comparisonType: ComparisonRangeType | null | undefined;
    activeSort: SortParams | null | undefined;
    stickyHeaderTop: number | null | undefined;
    onParamChange: (arg1: any, shouldMerge: boolean) => void;
};

type Props = StateProps & DispatchProps & OwnProps;

/*
 * Component representing the Summary tab of the Activity Report.
 */
export class ActivityReportSummaryComponent extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        this.fetchContent(this.props);
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (
            JSON.stringify(nextProps.filters) !== JSON.stringify(this.props.filters) ||
            nextProps.comparisonType !== this.props.comparisonType
        ) {
            this.fetchContent(nextProps);
        }
    }

    render() {
        return (
            <ActivityReportSummaryTable
                isLoading={this.props.isLoading}
                hasError={this.props.hasError}
                onErrorRetry={this.handleErrorRetry}
                data={this.props.data}
                totals={this.props.totals}
                stickyHeaderTop={this.props.stickyHeaderTop}
                onColumnSort={this.handleColumnSort}
                activeSort={this.props.activeSort}
            />
        );
    }

    handleErrorRetry = () => {
        this.fetchContent(this.props);
    };

    // The first click on an activity type should make it descending, then ascending, then disable
    handleColumnSort = (activityType: string) => {
        const {activeSort, onParamChange} = this.props;
        if (
            activeSort &&
            activeSort.by === activityType &&
            activeSort.direction === SORT_DIRECTION_ASCENDING
        ) {
            onParamChange(
                {
                    sort: {
                        by: USER_SORT_STRING,
                        direction: SORT_DIRECTION_DESCENDING,
                    },
                },
                false
            );

            return;
        }

        const direction =
            activeSort && activeSort.by === activityType
                ? SORT_DIRECTION_ASCENDING
                : SORT_DIRECTION_DESCENDING;

        const sort = {
            by: activityType,
            direction,
        } as const;

        onParamChange({sort}, false);
    };

    fetchContent = (props: Props) => {
        this.props.fetchContent(props.filters, props.comparisonType);
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);

export const ActivityReportSummary = connector(ActivityReportSummaryComponent);
